import { uniqBy, flatMap, isString, parseInt, first, isNil, get, isNumber, round, cloneDeep, isArray, isObject, pickBy, intersection, difference } from 'lodash';
import { DocumentRecord, DatabaseType, DatabaseRecord, DatabaseService, upgraded, GenericRestService, OfflineFirstDataService } from 'fitforce-offline-first';
import * as moment_ from 'moment';
import { ɵɵinject, ɵɵdefineInjectable, ɵsetClassMetadata, Injectable } from '@angular/core';
import { SyncLoggingService } from 'fitforce-logging';
import { v4 } from 'uuid';
import { HttpClient } from '@angular/common/http';
import { of, forkJoin, Subject } from 'rxjs';
import { map, concatMap, catchError, tap } from 'rxjs/operators';
import { patch, diff } from 'jiff';
import Ajv from 'ajv';

const moment = moment_;
var ResourceType;
(function (ResourceType) {
    ResourceType["Video"] = "video";
    ResourceType["Audio"] = "audio";
    ResourceType["Image"] = "image";
})(ResourceType || (ResourceType = {}));
const tierRowParams = ['sets', 'reps', 'load', 'time', 'distance', 'hold'];
const getDefaultParams = () => {
    return tierRowParams.map(param => {
        return {
            param,
            unit: '',
            type: 'text',
        };
    });
};
class ExerciseProgramDocument extends DocumentRecord {
    constructor(data) {
        super(data);
    }
}
ExerciseProgramDocument.recordName = 'ExerciseProgramDocument';
ExerciseProgramDocument.objectStoreName = 'exerciseProgramDocuments';
class ExerciseProgramDocumentConstants {
}
ExerciseProgramDocumentConstants.intArrayType = 'int-array';
ExerciseProgramDocumentConstants.strArrayType = 'str-array';
class ExerciseProgramDocumentCache extends ExerciseProgramDocument {
    constructor(program) {
        super({ key: program.key, document: program.document, version: program.version });
        this.syncedAt = moment();
        this.createdAt = program.createdAt;
        this.updatedAt = program.updatedAt;
        this.deletedAt = program.deletedAt;
    }
}
ExerciseProgramDocumentCache.recordName = 'ExerciseProgramDocumentCache';
ExerciseProgramDocumentCache.objectStoreName = 'exerciseProgramDocumentsCache';
ExerciseProgramDocumentCache.fields = ['version', 'document', 'lastSynced'];
ExerciseProgramDocumentCache.databaseType = DatabaseType.Document;
class ExerciseProgramDocumentUtils {
    static getPossibleParamsForExercise(exerciseId, programRef) {
        const constants = ExerciseProgramDocumentConstants;
        if (!exerciseId) {
            throw new Error('Invalid exercise id');
        }
        const exercise = programRef.exercises.find(ex => ex.id === exerciseId);
        if (!exercise) {
            throw new Error(`Exercise ${exerciseId} not found`);
        }
        const params = Object.keys(exercise.parameters);
        if (params.length > 0) {
            return params.map((key, value) => {
                return {
                    param: key,
                    unit: exercise.parameters[key].unit,
                    type: exercise.parameters[key].type === constants.intArrayType
                        ? 'number'
                        : exercise.parameters[key].type === constants.strArrayType
                            ? 'text'
                            : 'text',
                };
            });
        }
        else {
            return getDefaultParams();
        }
    }
    static getPossibleParamsFromTier(tier, programRef) {
        if (tier) {
            return uniqBy(flatMap(tier.rows, row => {
                return flatMap(row.exercises, exerciseId => {
                    const exercise = programRef.exercises.find(ex => ex.id === exerciseId);
                    return Object.keys(row.parameter_overrides).map((key, value) => {
                        // override parameter with tier template values if exist
                        const option = {
                            param: key,
                        };
                        if (row.parameter_overrides[key].unit) {
                            option.unit = row.parameter_overrides[key].unit;
                        }
                        else {
                            option.unit = exercise.parameters[key] ? exercise.parameters[key].unit : 'N/A';
                        }
                        if (row.parameter_overrides[key].type) {
                            option.type = row.parameter_overrides[key].type;
                        }
                        else {
                            option.type = exercise.parameters[key]
                                ? exercise.parameters[key].type === 'int-array'
                                    ? 'number'
                                    : exercise.parameters[key].type === 'str-array'
                                        ? 'text'
                                        : 'text'
                                : 'text';
                        }
                        return option;
                    });
                });
            }), param => param.param);
        }
        else {
            return getDefaultParams();
        }
    }
    static orderParams(params) {
        function compareFn(a, b) {
            const aParam = a.param ? a.param : a;
            const bParam = b.param ? b.param : b;
            if (aParam === 'sets') {
                return -1;
            }
            else if (aParam === 'reps') {
                if (bParam === 'sets') {
                    return 1;
                }
                else {
                    return -1;
                }
            }
            else if (aParam === 'load') {
                if (bParam === 'sets' || bParam === 'reps') {
                    return 1;
                }
                else {
                    return -1;
                }
            }
            else if (bParam === 'sets') {
                return 1;
            }
            else if (bParam === 'reps') {
                if (aParam === 'sets') {
                    return -1;
                }
                else {
                    return 1;
                }
            }
            else if (bParam === 'load') {
                if (aParam === 'sets' || aParam === 'reps') {
                    return -1;
                }
                else {
                    return 1;
                }
            }
            else {
                return aParam > bParam ? 1 : -1;
            }
        }
        return params.sort(compareFn);
    }
}

const moment$1 = moment_;
const rpeOptions = [
    { value: 0, description: 'Rest' },
    { value: 1, description: 'Very, Very Easy' },
    { value: 2, description: 'Easy' },
    { value: 3, description: 'Moderate' },
    { value: 4, description: 'Somewhat Hard' },
    { value: 5, description: 'Hard' },
    { value: 6, description: 'Hard' },
    { value: 7, description: 'Very Hard' },
    { value: 8, description: 'Very Hard' },
    { value: 9, description: 'Very Hard' },
    { value: 10, description: 'Maximal' },
];
class PlanDocument extends DocumentRecord {
    constructor(data) {
        super(data);
    }
}
PlanDocument.recordName = 'PlanDocument';
PlanDocument.objectStoreName = 'planDocuments';
class PlanDocumentCache extends PlanDocument {
    constructor(plan) {
        super({ key: plan.key, document: plan.document, version: plan.version });
        this.syncedAt = moment$1();
        this.createdAt = plan.createdAt;
        this.updatedAt = plan.updatedAt;
        this.deletedAt = plan.deletedAt;
    }
}
PlanDocumentCache.recordName = 'PlanDocumentCache';
PlanDocumentCache.objectStoreName = 'planDocumentsCache';
PlanDocumentCache.fields = ['version', 'userUUID', 'document', 'lastSynced'];
PlanDocumentCache.databaseType = DatabaseType.Document;
class PlanDocumentUtils {
    static getAcronym(name) {
        const words = name ? name.trim().split(/\s+/) : [];
        let toReturn = '';
        words.forEach(word => {
            const letter = word.substring(0, 1);
            if (letter.match(/[a-zA-Z0-9]/)) {
                toReturn += letter;
            }
        });
        toReturn = toReturn || (name.replace(/\s/g, '') + '--').substr(0, 2);
        return toReturn.toUpperCase().substr(0, 4);
    }
    static getSchedule(referenceDate, date) {
        let week = date.week() - referenceDate.week();
        if (date.week() - referenceDate.week() < 0) {
            week += 52;
        }
        let day = date.day() - referenceDate.day();
        if (day < 0) {
            day += 7;
            week--;
        }
        return {
            day,
            week,
        };
    }
    static getDate(referenceDate, day, week) {
        const parsedDay = isString(day) ? parseInt(day, 10) : day;
        const parsedWeek = isString(week) ? parseInt(week, 10) : week;
        let adjustedDay = parsedDay + referenceDate.day();
        let adjustedWeek = parsedWeek;
        if (adjustedDay > 7) {
            adjustedDay -= 7;
            adjustedWeek++;
        }
        const dayDiff = adjustedDay - referenceDate.day();
        const date = referenceDate.clone();
        date.add(adjustedWeek, 'w');
        date.add(dayDiff, 'd');
        return date;
    }
    /**
     * Searches the provided set of workouts and returns the first instance of the workout
     * matching the schedule parameters
     * @param workouts set of workouts to search
     * @param schedule schedule of workout to find
     */
    static findWorkout(workouts, schedule) {
        if (workouts) {
            const allWorkouts = this.findWorkouts(workouts, schedule);
            if (allWorkouts && allWorkouts.length > 0) {
                return first(allWorkouts);
            }
        }
        return null;
    }
    /**
     * Searches the provided set of workouts and returns all workouts that match the schedule parameter
     * @param workouts set of workouts to search
     * @param schedule schedule of workouts to find
     */
    static findWorkouts(workouts, schedule) {
        if (workouts) {
            return workouts.filter(workout => {
                if (workout.hasOwnProperty('schedule')) {
                    // tslint:disable-next-line: prefer-for-of
                    for (let i = 0; i < workout.schedule.week.length; i++) {
                        // tslint:disable-next-line: prefer-for-of
                        for (let j = 0; j < workout.schedule.day.length; j++) {
                            if (workout.schedule.week[i] === schedule.week && workout.schedule.day[j] === schedule.day) {
                                return true;
                            }
                        }
                    }
                }
                return false;
            });
        }
        return null;
    }
    static findPhase(phases, schedule) {
        if (phases) {
            return phases.find(phase => {
                return ((phase.start_week < schedule.week && phase.end_week > schedule.week) ||
                    (phase.start_week === schedule.week && phase.end_week > schedule.week && phase.start_day <= schedule.day) ||
                    (phase.end_week === schedule.week && phase.start_week < schedule.week && phase.end_day >= schedule.day) ||
                    (phase.start_week === schedule.week &&
                        phase.end_week === schedule.week &&
                        phase.start_day <= schedule.day &&
                        phase.end_day >= schedule.day));
            });
        }
        return null;
    }
    static compare(a, b) {
        return (!a && !b) || (a && b && a.id === b.id && a.name === b.name);
    }
    static calcPercent1RM(workout) {
        const loadParameter = 'load';
        let percent1RM = 0;
        if (workout && workout.tiers) {
            const tier3 = workout.tiers.find(tier => tier.order === 3);
            let loadSum = 0;
            if (tier3) {
                const totalLoads = this.numOfRowsWithLoads(tier3.rows);
                tier3.rows.forEach((row, index) => {
                    if (index === 0) {
                        const params = row.parameter_selections;
                        if (params && !isNil(get(params, loadParameter))) {
                            if (isString(params[loadParameter])) {
                                loadSum += +params[loadParameter].substring(0, params[loadParameter].length - 1);
                            }
                            else if (isNumber(params[loadParameter]) && params[loadParameter] > -1) {
                                loadSum += params[loadParameter];
                            }
                        }
                    }
                });
                if (totalLoads > 0) {
                    percent1RM = round(loadSum / totalLoads);
                }
            }
        }
        return percent1RM;
    }
    static numOfRowsWithLoads(rows) {
        let sum = 0;
        rows.forEach((row, index) => {
            if (index === 0 && row.parameter_selections) {
                if (row.parameter_selections.hasOwnProperty('load')) {
                    sum++;
                }
            }
        });
        return sum;
    }
    static getHex(colorClass) {
        if (!colorClass) {
            return '#818181';
        }
        let elementUsingStyling = document.querySelector('.' + colorClass);
        let createdTempElement = false;
        // if there wasn't anything using this color yet, create a placeholder element
        if (!elementUsingStyling) {
            elementUsingStyling = document.createElement('div');
            elementUsingStyling.classList.add(colorClass);
            document.body.appendChild(elementUsingStyling);
            createdTempElement = true;
        }
        const colorCode = window.getComputedStyle(elementUsingStyling).getPropertyValue('background-color');
        if (createdTempElement) {
            document.body.removeChild(elementUsingStyling);
        }
        return colorCode.indexOf('rgb') === 0 ? this.rgbToHex(colorCode) : colorCode;
    }
    static rgbToHex(rgb) {
        rgb = rgb.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);
        return rgb && rgb.length === 4
            ? '#' +
                ('0' + parseInt(rgb[1], 10).toString(16)).slice(-2) +
                ('0' + parseInt(rgb[2], 10).toString(16)).slice(-2) +
                ('0' + parseInt(rgb[3], 10).toString(16)).slice(-2)
            : '';
    }
    static sortTiers(a, b) {
        return a.order > b.order ? 1 : a.order === b.order ? 0 : -1;
    }
}

class PlanMetadata extends DatabaseRecord {
}
PlanMetadata.recordName = 'PlanMetadata';
PlanMetadata.objectStoreName = 'plans';
PlanMetadata.fields = [
    'version',
    'userUUID',
    'id',
    'name',
    'notes',
    'numWeeks',
    'startDate',
    'isDownloaded',
    'sharedWithUsers',
    'publishedToGroups',
    'createdByUser',
];
PlanMetadata.databaseType = DatabaseType.Document;

const moment$2 = moment_;
class UserLibraryDocument extends DocumentRecord {
    constructor(data) {
        super(data);
    }
}
UserLibraryDocument.recordName = 'UserLibraryDocument';
UserLibraryDocument.objectStoreName = 'userLibraryDocuments';
class UserLibraryDocumentCache extends UserLibraryDocument {
    constructor(lib) {
        super({ key: lib.key, document: lib.document, version: lib.version });
        this.syncedAt = moment$2();
        this.createdAt = lib.createdAt;
        this.updatedAt = lib.updatedAt;
        this.deletedAt = lib.deletedAt;
    }
}
UserLibraryDocumentCache.recordName = 'UserLibraryDocumentCache';
UserLibraryDocumentCache.objectStoreName = 'userLibraryDocumentsCache';
UserLibraryDocumentCache.fields = ['version', 'userUUID', 'document', 'lastSynced'];
UserLibraryDocumentCache.databaseType = DatabaseType.Document;

class WorkoutSessionDocument extends DocumentRecord {
    constructor(data) {
        super(data);
    }
}
WorkoutSessionDocument.recordName = 'WorkoutSessionDocument';
WorkoutSessionDocument.objectStoreName = 'workoutSessionDocuments';

class ObservationDocument extends DocumentRecord {
    constructor(data) {
        super(data);
    }
}
ObservationDocument.recordName = 'ObservationDocument';
ObservationDocument.objectStoreName = 'observationDocuments';

const objectStores = {
    exerciseProgramDocuments: { keyPath: 'key' },
    exerciseProgramDocumentsCache: { keyPath: 'key' },
    planDocuments: { keyPath: 'key' },
    planDocumentsCache: { keyPath: 'key' },
    plans: { keyPath: 'key' },
    workoutSessionDocuments: { keyPath: 'key' },
    userLibraryDocuments: { keyPath: 'key' },
    userLibraryDocumentsCache: { keyPath: 'key' },
    observationDocuments: { keyPath: 'key' },
};
class DocumentDatabaseService extends DatabaseService {
    constructor(syncLog) {
        super(syncLog);
        this.options = {
            dbName: DocumentDatabaseService.dbName,
            version: DocumentDatabaseService.version,
            objectStores,
            seed: {},
            upgraded,
        };
    }
}
DocumentDatabaseService.version = 5;
DocumentDatabaseService.dbName = 'fitforce-planner-documents';
DocumentDatabaseService.ɵfac = function DocumentDatabaseService_Factory(t) { return new (t || DocumentDatabaseService)(ɵɵinject(SyncLoggingService)); };
DocumentDatabaseService.ɵprov = ɵɵdefineInjectable({ token: DocumentDatabaseService, factory: DocumentDatabaseService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵsetClassMetadata(DocumentDatabaseService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: SyncLoggingService }]; }, null); })();

const moment$3 = moment_;
var DocumentModelType;
(function (DocumentModelType) {
    DocumentModelType[DocumentModelType["Phase"] = 0] = "Phase";
    DocumentModelType[DocumentModelType["Plan"] = 1] = "Plan";
    DocumentModelType[DocumentModelType["PlannedTier"] = 2] = "PlannedTier";
    DocumentModelType[DocumentModelType["PlannedTierRow"] = 3] = "PlannedTierRow";
    DocumentModelType[DocumentModelType["Schedule"] = 4] = "Schedule";
    DocumentModelType[DocumentModelType["Template"] = 5] = "Template";
    DocumentModelType[DocumentModelType["UserLibrary"] = 6] = "UserLibrary";
    DocumentModelType[DocumentModelType["Workout"] = 7] = "Workout";
    DocumentModelType[DocumentModelType["Observation"] = 8] = "Observation";
    DocumentModelType[DocumentModelType["Exercise"] = 9] = "Exercise";
    DocumentModelType[DocumentModelType["VideoResource"] = 10] = "VideoResource";
})(DocumentModelType || (DocumentModelType = {}));
class DocumentModelFactoryService {
    constructor() {
        this.inititalized = false;
        this.init();
    }
    init() {
        if (!this.inititalized) {
            this.builders = new Map();
            this.builders.set(DocumentModelType.Phase, PhaseBuilder);
            this.builders.set(DocumentModelType.Plan, PlanBuilder);
            this.builders.set(DocumentModelType.PlannedTier, PlannedTierBuilder);
            this.builders.set(DocumentModelType.PlannedTierRow, PlannedTierRowBuilder);
            this.builders.set(DocumentModelType.Schedule, ScheduleBuilder);
            this.builders.set(DocumentModelType.Template, TemplateBuilder);
            this.builders.set(DocumentModelType.UserLibrary, UserLibraryDocumentBuilder);
            this.builders.set(DocumentModelType.Workout, WorkoutBuilder);
            this.builders.set(DocumentModelType.Observation, ObservationBuilder);
            this.builders.set(DocumentModelType.Exercise, ExerciseBuilder);
            this.builders.set(DocumentModelType.VideoResource, VideoResourceBuilder);
        }
    }
    build(type) {
        const builder = this.builders.get(type);
        return builder ? builder() : null;
    }
    copy(source) {
        const copy = cloneDeep(source);
        copy.id = v4();
        return copy;
    }
}
DocumentModelFactoryService.ɵfac = function DocumentModelFactoryService_Factory(t) { return new (t || DocumentModelFactoryService)(); };
DocumentModelFactoryService.ɵprov = ɵɵdefineInjectable({ token: DocumentModelFactoryService, factory: DocumentModelFactoryService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵsetClassMetadata(DocumentModelFactoryService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return []; }, null); })();
function PhaseBuilder() {
    return {
        id: v4(),
        end_day: null,
        end_week: null,
        name: null,
        start_day: null,
        start_week: null,
    };
}
function PlanBuilder() {
    return {
        id: v4(),
        exercise_program_id: null,
        exercise_program_version: null,
        exercises: [],
        name: null,
        num_weeks: null,
        phases: [],
        start_date: null,
        type: 'Plan',
        version: null,
        workouts: [],
    };
}
function PlannedTierBuilder() {
    return {
        id: v4(),
        name: 'Custom Tier 1',
        rows: [],
        order: 1,
        notes: '',
    };
}
function PlannedTierRowBuilder() {
    return {
        id: v4(),
        exercise_id: null,
        exercise_name: null,
        notes: '',
        parameter_selections: {},
    };
}
function ScheduleBuilder() {
    return {
        day: [],
        week: [],
    };
}
function TemplateBuilder() {
    return {
        id: v4(),
        name: null,
        template: null,
    };
}
function UserLibraryDocumentBuilder() {
    return {
        id: v4(),
        exercise_program_id: null,
        exercise_program_version: null,
        name: 'User Library',
        rows: [],
        tiers: [],
        type: 'UserLibrary',
        user: null,
        version: null,
        workouts: [],
    };
}
function WorkoutBuilder() {
    return {
        id: v4(),
        name: 'Custom Day',
        schedule: null,
        tiers: [],
        version: 0,
        est_completion_time_ms: -1,
        est_rpe: -1,
    };
}
function ObservationBuilder() {
    return {
        id: v4(),
        measure: null,
        result: null,
        timestamp: moment$3().toISOString(),
    };
}
function ExerciseBuilder() {
    return {
        id: v4(),
        name: null,
        parameters: {},
        resources: [],
        exercise_categories: [],
    };
}
function VideoResourceBuilder() {
    return {
        id: v4(),
        hls_url: null,
        name: '',
        title: '',
        type: ResourceType.Video,
    };
}

class DocumentModelFactoryServiceStub {
    build() {
        return {};
    }
    copy(source) {
        return cloneDeep(source);
    }
}

const exerciseProgramBaseUrl = 'ExerciseProgramDocument';
const planDocumentBaseUrl = 'PlanDocument';
const workoutSessionDocumentBaseUrl = 'WorkoutSessionDocument';
const userLibraryBaseUrl = 'UserLibraryDocument';
const observationBaseUrl = 'ObservationDocument';
const moment$4 = moment_;
class RestOptions {
}
function validateDocumentApiResponse(response) {
    return response.hasOwnProperty('uuid') && response.hasOwnProperty('document') && response.hasOwnProperty('version');
}
class DocumentRestService extends GenericRestService {
    constructor(httpClient, environment) {
        super(httpClient);
        this.baseUrl = environment.api_url;
    }
    getExercisePrograms() {
        return this.get(`${exerciseProgramBaseUrl}/`).pipe(map((response) => {
            if (!response.hasOwnProperty('documents')) {
                throw new Error('GET api/ExerciseProgramDocument/: Invalid API response');
            }
            return response.documents;
        }));
    }
    getExerciseProgram(uuid) {
        return this.getDocument(uuid, exerciseProgramBaseUrl, (data) => new ExerciseProgramDocument(data));
    }
    getExerciseProgramDiff(program) {
        return this.getDocumentDiff(program, exerciseProgramBaseUrl);
    }
    getExerciseProgramDocumentLatestVersion(uuid) {
        return this.get(`${exerciseProgramBaseUrl}/${uuid}/LatestVersion`).pipe(map(res => res.version));
    }
    patchExerciseProgramDocument(uuid, patch) {
        return this.patchDocument(uuid, exerciseProgramBaseUrl, patch);
    }
    getPlans(onlyPublished) {
        return this.get(`${planDocumentBaseUrl}/${onlyPublished ? '?onlyPublishedPlans=true' : ''}`).pipe(concatMap(response => {
            if (!response.hasOwnProperty('plans')) {
                throw new Error(`GET api/${planDocumentBaseUrl}/: Invalid API response`);
            }
            if (response.plans.length === 0) {
                return of([]);
            }
            if (response.totalPages > 1) {
                const obs = [];
                for (let i = 2; i <= response.totalPages; i++) {
                    obs.push(this.get(`${planDocumentBaseUrl}/?pageNumber=${i}&limitPerPage=${50}${onlyPublished ? '&onlyPublishedPlans=true' : ''}`));
                }
                return forkJoin(obs).pipe(map((results) => {
                    let plans = response.plans;
                    results.forEach((result) => {
                        if (!result.hasOwnProperty('plans')) {
                            throw new Error(`GET api/${planDocumentBaseUrl}/: Invalid API response`);
                        }
                        if (result.plans.length > 0) {
                            plans = plans.concat(result.plans);
                        }
                    });
                    return plans.map(plan => this.buildPlanMetadata(plan));
                }));
            }
            else {
                return of(response.plans.map(plan => this.buildPlanMetadata(plan)));
            }
        }));
    }
    getPlanSharedUsers(uuid) {
        return this.get(`${planDocumentBaseUrl}/${uuid}/getSharedUsers`).pipe(map(users => {
            if (users.hasOwnProperty('sharedWithUsers') && users.sharedWithUsers.length > 0) {
                return users.sharedWithUsers.map(user => {
                    return {
                        uuid: user.userUUID,
                        email: user.userEmail,
                        firstName: user.userFirstName,
                        lastName: user.userLastName,
                    };
                });
            }
            else {
                return [];
            }
        }));
    }
    getPlanPublishedGroups(uuid) {
        return this.get(`Group/getGroupsWithPublishedPlanDocument/${uuid}`).pipe(map(response => {
            if (response.hasOwnProperty('groups') && response.groups.length > 0) {
                return response.groups.map(group => {
                    return {
                        uuid: group.uuid,
                        name: group.name,
                    };
                });
            }
            else {
                return [];
            }
        }));
    }
    getGroupPublishedPlans(uuid) {
        return this.get(`Group/getPlanDocumentsPublishedToGroup/${uuid}`).pipe(map(response => {
            if (!response.hasOwnProperty('plans')) {
                throw new Error(`GET api/Group/getPlanDocumentsPublishedToGroup/${uuid} : Invalid API response`);
            }
            if (response.plans.length === 0) {
                return [];
            }
            return response.plans.map((plan) => this.buildPlanMetadata(plan));
        }));
    }
    getPlanDocument(uuid) {
        return this.getDocument(uuid, planDocumentBaseUrl, (data) => new PlanDocument(data));
    }
    getPlanDocumentDiff(plan) {
        return this.getDocumentDiff(plan, planDocumentBaseUrl);
    }
    getPlanDocumentLatestVersion(uuid) {
        return this.get(`${planDocumentBaseUrl}/${uuid}/LatestVersion`).pipe(map(res => res.version));
    }
    createPlanDocument(plan) {
        return this.createDocument(plan, planDocumentBaseUrl);
    }
    deletePlanDocument(uuid) {
        return this.deleteDocument(uuid, planDocumentBaseUrl);
    }
    patchPlanDocument(uuid, patch) {
        return this.patchDocument(uuid, planDocumentBaseUrl, patch);
    }
    getUserLibraryDocumentIds() {
        return this.get(`${userLibraryBaseUrl}/`).pipe(map((response) => {
            if (!response.hasOwnProperty('documents')) {
                throw new Error('GET api/UserLibraryDocument/: Invalid API response');
            }
            return response.documents;
        }));
    }
    getUserLibraryDocument(uuid) {
        return this.getDocument(uuid, userLibraryBaseUrl, (data) => new UserLibraryDocument(data));
    }
    getUserLibraryDocumentDiff(lib) {
        return this.getDocumentDiff(lib, userLibraryBaseUrl);
    }
    getUserLibraryDocumentLatestVersion(uuid) {
        return this.get(`${userLibraryBaseUrl}/${uuid}/LatestVersion`).pipe(map(res => res.version));
    }
    createUserLibraryDocument(plan) {
        return this.createDocument(plan, userLibraryBaseUrl);
    }
    deleteUserLibraryDocument(uuid) {
        return this.deleteDocument(uuid, userLibraryBaseUrl);
    }
    patchUserLibraryDocument(uuid, patch) {
        return this.patchDocument(uuid, userLibraryBaseUrl, patch);
    }
    sharePlan(uuid, userUUIDs) {
        return this.post(`${planDocumentBaseUrl}/${uuid}/share`, {
            userUUIDList: userUUIDs,
        }).pipe(map(() => null));
    }
    unsharePlan(uuid, userUUIDs) {
        return this.post(`${planDocumentBaseUrl}/${uuid}/unshare`, {
            userUUIDList: userUUIDs,
        }).pipe(map(() => null));
    }
    publishPlan(uuid, groupUUIDs) {
        return this.post(`Group/publishPlanDocument`, {
            plandocUUID: uuid,
            groupUUIDList: groupUUIDs,
        }).pipe(map(() => null));
    }
    unpublishPlan(uuid, groupUUIDs) {
        return this.post(`Group/unpublishPlanDocument`, {
            plandocUUID: uuid,
            groupUUIDList: groupUUIDs,
        }).pipe(map(() => null));
    }
    downloadPlaycard(planDocumentUUID, planDayId, version) {
        return this.post('DocumentXLSExport/', {
            planDocumentUUID,
            planDayId,
            version,
        }).pipe(map((res) => res.taskUUID), catchError(e => {
            return this.handleError(e);
        }));
    }
    checkPlaycardTask(taskUUID) {
        return this.http
            .get(this.buildRoute('DocumentXLSExport/status/' + taskUUID), {
            responseType: 'blob',
        })
            .pipe(catchError(e => {
            return this.handleError(e);
        }));
    }
    getWorkoutSessionDocumentIds() {
        return this.get(`${workoutSessionDocumentBaseUrl}/`).pipe(map((response) => {
            if (!response.hasOwnProperty('documents')) {
                throw new Error('GET api/WorkoutSessionDocument/: Invalid API response');
            }
            return response.documents;
        }));
    }
    getWorkoutSessionDocument(uuid) {
        return this.getDocument(uuid, workoutSessionDocumentBaseUrl, (data) => new WorkoutSessionDocument(data));
    }
    createWorkoutSessionDocument(workoutSession) {
        return this.createDocument(workoutSession, workoutSessionDocumentBaseUrl);
    }
    getObservationDocumentIds() {
        return this.get(`${observationBaseUrl}/`).pipe(map((response) => {
            if (!response.hasOwnProperty('documents')) {
                throw new Error('GET api/ObservationDocument/: Invalid API response');
            }
            return response.documents;
        }));
    }
    getObservationDocument(uuid) {
        return this.getDocument(uuid, observationBaseUrl, (data) => new ObservationDocument(data));
    }
    createObservationDocument(observation) {
        return this.createDocument(observation, observationBaseUrl);
    }
    getDocument(uuid, baseUrl, init) {
        return this.get(`${baseUrl}/${uuid}`).pipe(map((response) => {
            if (!validateDocumentApiResponse(response)) {
                throw new Error(`GET api/${baseUrl}/${uuid}: Invalid API response`);
            }
            const record = init({
                key: response.uuid,
                version: response.version,
                document: JSON.parse(response.document),
            });
            return record;
        }));
    }
    getDocumentDiff(record, baseUrl) {
        return this.get(`${baseUrl}/${record.uuid}/Diff?fromVersion=${record.version}`).pipe(map(res => {
            if (res.hasOwnProperty('patchStream') && res.hasOwnProperty('toVersion') && isArray(res.patchStream)) {
                const patches = res.patchStream;
                let patch = [];
                patches.forEach(patchStr => {
                    if (isString(patchStr)) {
                        patch = patch.concat(JSON.parse(patchStr));
                    }
                });
                return { version: res.toVersion, patch };
            }
            return { version: res.toVersion, patch: [] };
        }));
    }
    createDocument(record, baseUrl) {
        return this.post(`${baseUrl}/${record.uuid}`, {
            document: JSON.stringify(record.document),
        }).pipe(map(() => null));
    }
    deleteDocument(uuid, baseUrl) {
        return this.delete(`${baseUrl}/${uuid}`).pipe(map(() => null));
    }
    patchDocument(uuid, baseUrl, patch) {
        return this.patch(`${baseUrl}/${uuid}`, {
            patchString: JSON.stringify(patch),
        }).pipe(map(res => res.version));
    }
    buildPlanMetadata(plan) {
        const planMetadata = new PlanMetadata({
            key: plan.documentUUID,
        });
        planMetadata.name = plan.name;
        planMetadata.numWeeks = parseInt(plan.numWeeks, 10);
        planMetadata.notes = plan.notes;
        planMetadata.startDate = moment$4(plan.startDate);
        planMetadata.userUUID = plan.createdByUserUUID;
        planMetadata.version = parseInt(plan.documentVersion, 10);
        planMetadata.createdByUser = {
            email: plan.createdByUser.userEmail,
            firstName: plan.createdByUser.userFirstName,
            lastName: plan.createdByUser.userLastName,
            uuid: plan.createdByUser.userUUID,
        };
        planMetadata.sharedWithUsers = plan.sharedWithUsers.map(user => {
            return {
                email: user.userEmail,
                firstName: user.userFirstName,
                lastName: user.userLastName,
                uuid: user.userUUID,
            };
        });
        planMetadata.publishedToGroups = plan.publishedToGroups.map(group => {
            return {
                uuid: group.uuid,
                name: group.name,
            };
        });
        return planMetadata;
    }
}
DocumentRestService.ɵfac = function DocumentRestService_Factory(t) { return new (t || DocumentRestService)(ɵɵinject(HttpClient), ɵɵinject(RestOptions)); };
DocumentRestService.ɵprov = ɵɵdefineInjectable({ token: DocumentRestService, factory: DocumentRestService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵsetClassMetadata(DocumentRestService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return [{ type: HttpClient }, { type: RestOptions }]; }, null); })();

const diffOptions = {
    invertible: false,
    hash: (x) => {
        if (x && x.id) {
            return x.id;
        }
        else {
            return isObject(x) ? JSON.stringify(pickBy(x, p => !isArray(p))) : isArray(x) ? JSON.stringify(x) : x;
        }
    },
};
class DocumentSyncService extends OfflineFirstDataService {
    constructor(documentRestService, offline, syncLog, observer) {
        super(offline, syncLog, observer);
        this.documentRestService = documentRestService;
        this.planUpdates = new Subject();
    }
    /**
     * Prepares to sync with the server.
     * Initializes progress counters.
     */
    prepareSync() {
        this.syncLog.info('Preparing to sync documents with the server.');
        return this.offline.getAll(PlanMetadata).pipe(tap((plans) => {
            this.syncsDone = 0;
            this.syncsTotal = 2; // add 2 for restoreData calls
            const plansToSync = plans.filter(plan => plan.isNew || plan.isUpdated);
            this.syncsTotal += plansToSync ? plansToSync.length : 0;
            const plansToDelete = plans.filter(plan => plan.isDeleted);
            this.syncsTotal += plansToDelete ? plansToDelete.length : 0;
            const plansToPull = plans.filter(plan => plan.isDownloaded);
            // remove deleted plans since they won't be pulled
            this.syncsTotal += (plansToPull ? plansToPull.length : 0) - (plansToDelete ? plansToDelete.length : 0);
        }));
    }
    /**
     * Pushes any new plan documents, updates to plan documents, or deletes to the server
     * Pushes any updates to user library to the server
     */
    push() {
        this.syncLog.info('Saving document changes to the server.');
        return forkJoin(this.pushPlans().pipe(concatMap(() => this.pushPlanMetadata())), this.pushUserLibary());
    }
    /**
     * Pushes new and updated plan documents to the server.
     * In the event of a version mismatch from the server, a "take mine" approach
     * is taken when pushing updates
     *
     *  @param plans optional observable yielding list of plans to push
     */
    pushPlans(plans) {
        return this.offline.getAll(PlanDocumentCache).pipe(concatMap(cache => {
            return this.syncDirty(plans || PlanDocument, {
                model: PlanDocument,
                created: plan => this.documentRestService.createPlanDocument(plan),
                updated: plan => {
                    return this.documentRestService.getPlanDocumentLatestVersion(plan.uuid).pipe(concatMap(latestVersion => {
                        const cachedPlan = cache.find(p => p.uuid === plan.uuid);
                        if (latestVersion !== plan.version) {
                            // if local version does not match remote, construct remote from patch stream and send
                            // diff with local as patch to server
                            return this.documentRestService.getPlanDocumentDiff(plan).pipe(concatMap(res => {
                                const remotePlan = patch(res.patch, cachedPlan.document);
                                const patch$1 = diff(remotePlan, plan.document, diffOptions);
                                if (patch$1 && patch$1.length > 0) {
                                    return this.documentRestService.patchPlanDocument(plan.uuid, patch$1).pipe(concatMap(version => {
                                        plan.version = version;
                                        plan.document.version = version;
                                        return this.offline.save(PlanDocument, plan);
                                    }));
                                }
                                return of(null);
                            }));
                        }
                        else {
                            if (cachedPlan) {
                                const patch = diff(cachedPlan.document, plan.document, diffOptions);
                                if (patch && patch.length > 0) {
                                    return this.documentRestService.patchPlanDocument(plan.uuid, patch).pipe(concatMap(version => {
                                        plan.version = version;
                                        plan.document.version = version;
                                        return this.offline.save(PlanDocument, plan);
                                    }));
                                }
                            }
                            return of(null);
                        }
                    }));
                },
                deleted: () => of(null),
            }).pipe(concatMap(syncedPlans => this.saveCache(syncedPlans)), tap(() => this.updateTotalProgress(1)));
        }));
    }
    /**
     * Pushes any deletes to the server.
     * @param plans optional observable yielding list of plans to push
     */
    pushPlanMetadata(plans) {
        return this.syncDirty(plans || PlanMetadata, {
            model: PlanMetadata,
            created: () => of(null),
            updated: () => of(null),
            deleted: planMetadata => this.documentRestService.deletePlanDocument(planMetadata.uuid),
        });
    }
    /**
     * Pushes a plan to the server.
     * @param uuid plan uuid
     */
    pushPlan(uuid) {
        return this.offline.getRecord(PlanMetadata, uuid).pipe(concatMap(planMetadata => {
            if (planMetadata.isDirty) {
                return this.pushPlans(this.offline.getRecord(PlanDocument, uuid).pipe(map(plan => [plan]))).pipe(concatMap(() => this.pushPlanMetadata(of([planMetadata]))));
            }
            else {
                this.updateTotalProgress(1);
                return of(null);
            }
        }));
    }
    /**
     * Pushes a user library document to the server
     * @param uuid user library document uuid
     */
    // TODO: make more generic
    pushUserLibary() {
        return this.offline.getAll(UserLibraryDocumentCache).pipe(concatMap(cache => {
            return this.syncDirty(UserLibraryDocument, {
                model: UserLibraryDocument,
                created: lib => this.documentRestService.createUserLibraryDocument(lib),
                updated: lib => {
                    return this.documentRestService.getUserLibraryDocumentLatestVersion(lib.uuid).pipe(concatMap(latestVersion => {
                        const cached = cache.find(c => c.uuid === lib.uuid);
                        if (latestVersion !== lib.version) {
                            // if local version does not match remote, construct remote from patch stream and send
                            // diff with local as patch to server
                            return this.documentRestService.getUserLibraryDocumentDiff(lib).pipe(concatMap(res => {
                                const remote = patch(res.patch, cached.document);
                                const patch$1 = diff(remote, lib.document, diffOptions);
                                if (patch$1 && patch$1.length > 0) {
                                    return this.documentRestService.patchUserLibraryDocument(lib.uuid, patch$1).pipe(concatMap(version => {
                                        lib.version = version;
                                        lib.document.version = version;
                                        return this.offline.save(UserLibraryDocument, lib);
                                    }));
                                }
                                return of(null);
                            }));
                        }
                        else {
                            if (cached) {
                                const patch = diff(cached.document, lib.document, diffOptions);
                                if (patch && patch.length > 0) {
                                    return this.documentRestService.patchUserLibraryDocument(lib.uuid, patch).pipe(concatMap(version => {
                                        lib.version = version;
                                        lib.document.version = version;
                                        return this.offline.save(UserLibraryDocument, lib);
                                    }));
                                }
                            }
                            return of(null);
                        }
                    }));
                },
                deleted: lib => this.documentRestService.deleteUserLibraryDocument(lib.uuid),
            }).pipe(concatMap(lib => this.saveUserLibraryCache(lib)), map(() => null));
        }));
    }
    /**
     * Pulls any updates to document models from the server.
     */
    pull(groupUUIDs) {
        this.syncLog.info('Fetching documents from the server and saving to offline database.');
        return forkJoin(this.pullExercisePrograms(), this.pullPlanMetadata(groupUUIDs), this.pullUserLibrary()).pipe(concatMap(() => {
            return forkJoin(this.offline.getAll(PlanMetadata), this.offline.getAll(PlanDocument)).pipe(concatMap((result) => {
                const planMetadata = result[0];
                const plans = result[1];
                const plansToPull = intersection(planMetadata.map(p => p.uuid), plans.map(p => p.uuid));
                const plansToDelete = difference(plans.map(p => p.uuid), planMetadata.map(p => p.uuid));
                const obs = [];
                if (plansToPull.length > 0) {
                    obs.push(this.pullPlans(plansToPull).pipe(concatMap(() => forkJoin(plans.map(plan => this.markDownloaded(plan.uuid))))));
                }
                if (plansToDelete.length > 0) {
                    obs.push(...plansToDelete.map(uuid => this.offline.deleteByKey(PlanDocument, uuid)));
                }
                if (obs.length > 0) {
                    return forkJoin(obs);
                }
                else {
                    return of(null);
                }
            }));
        }));
    }
    /**
     * Pulls all exercise program documents from the server.
     */
    pullExercisePrograms() {
        return this.documentRestService.getExercisePrograms().pipe(concatMap(uuids => {
            if (uuids.length > 0) {
                return this.getManyDocuments({
                    model: ExerciseProgramDocument,
                    uuids,
                    clear: true,
                    request: uuid => this.documentRestService.getExerciseProgram(uuid),
                    diffRequest: record => this.documentRestService.getExerciseProgramDiff(record).pipe(map(res => {
                        if (res.patch.length > 0) {
                            record.document = patch(res.patch, record.document);
                        }
                        record.version = res.version;
                        return record;
                    })),
                });
            }
            else {
                return of([]);
            }
        }), concatMap(program => this.saveExerciseProgramDocumentCache(program)));
    }
    /**
     * Pushes exercise programs to the server
     */
    pushExerciseProgram() {
        return this.offline.getAll(ExerciseProgramDocumentCache).pipe(concatMap(cache => {
            return this.syncDirty(ExerciseProgramDocument, {
                model: ExerciseProgramDocument,
                created: () => of(null),
                updated: program => {
                    return this.documentRestService.getExerciseProgramDocumentLatestVersion(program.uuid).pipe(concatMap(latestVersion => {
                        const cached = cache.find(c => c.uuid === program.uuid);
                        if (latestVersion !== program.version) {
                            // if local version does not match remote, construct remote from patch stream and send
                            // diff with local as patch to server
                            return this.documentRestService.getExerciseProgramDiff(program).pipe(concatMap(res => {
                                const remote = patch(res.patch, cached.document);
                                const patch$1 = diff(remote, program.document, diffOptions);
                                if (patch$1 && patch$1.length > 0) {
                                    return this.documentRestService.patchExerciseProgramDocument(program.uuid, patch$1).pipe(concatMap(version => {
                                        program.version = version;
                                        program.document.version = version;
                                        return this.offline.save(ExerciseProgramDocument, program);
                                    }));
                                }
                                return of(null);
                            }));
                        }
                        else {
                            if (cached) {
                                const patch = diff(cached.document, program.document, diffOptions);
                                if (patch && patch.length > 0) {
                                    return this.documentRestService.patchExerciseProgramDocument(program.uuid, patch).pipe(concatMap(version => {
                                        program.version = version;
                                        program.document.version = version;
                                        return this.offline.save(ExerciseProgramDocument, program);
                                    }));
                                }
                            }
                            return of(null);
                        }
                    }));
                },
                deleted: () => of(null),
            }).pipe(concatMap(program => this.saveExerciseProgramDocumentCache(program)), map(() => null));
        }));
    }
    /**
     * Pulls all plan metadata from the server
     */
    pullPlanMetadata(groupUUIDs) {
        if (groupUUIDs && groupUUIDs.length > 0) {
            const requests = groupUUIDs.map(groupUUID => this.documentRestService.getGroupPublishedPlans(groupUUID));
            return this.getMany({
                model: PlanMetadata,
                clear: true,
                request: forkJoin(requests).pipe(map(res => {
                    const plans = [];
                    res.forEach(publishedPlans => {
                        publishedPlans.forEach(plan => {
                            if (!plans.some(p => p.uuid === plan.uuid)) {
                                plans.push(plan);
                            }
                        });
                    });
                    return plans;
                })),
            });
        }
        else {
            return this.getMany({
                model: PlanMetadata,
                clear: true,
                request: this.documentRestService.getPlans(),
            });
        }
    }
    /**
     * Pulls plan documents with provided uuids from the server
     * @param uuids plan uuids
     */
    pullPlans(uuids, getLatestVersion) {
        return this.getManyDocuments({
            model: PlanDocument,
            uuids,
            request: uuid => this.documentRestService.getPlanDocument(uuid),
            diffRequest: record => {
                if (getLatestVersion) {
                    return this.documentRestService.getPlanDocument(record.uuid);
                }
                else {
                    return this.documentRestService.getPlanDocumentDiff(record).pipe(tap(res => this.planUpdates.next({ plan: record, patch: res })), concatMap(res => {
                        record.version = res.version;
                        if (res.patch.length > 0) {
                            // if there are changes from the server, apply to cache and take diff of local record
                            return this.offline.getRecord(PlanDocumentCache, record.uuid).pipe(map(cache => {
                                // apply remote changes to local cache
                                const patched = patch(res.patch, cache.document);
                                // take diff of local document and patched cache
                                const diff$1 = diff(record.document, patched, diffOptions);
                                if (diff$1.length > 0) {
                                    // if local document is still different from cache, apply diff to local document
                                    record.document = patch(diff$1, record.document);
                                }
                                return record;
                            }));
                        }
                        else {
                            return of(record);
                        }
                    }));
                }
            },
            clear: false,
        }).pipe(concatMap(plans => this.saveCache(plans)), tap(() => this.updateTotalProgress(uuids.length)));
    }
    pullWorkoutSessions() {
        return this.documentRestService.getWorkoutSessionDocumentIds().pipe(concatMap(ids => {
            if (ids.length > 0) {
                return this.getManyDocuments({
                    model: WorkoutSessionDocument,
                    uuids: ids,
                    clear: true,
                    request: uuid => this.documentRestService.getWorkoutSessionDocument(uuid),
                    diffRequest: record => of(record),
                });
            }
            else {
                return of([]);
            }
        }));
    }
    pushWorkoutSessions() {
        return this.syncDirty(WorkoutSessionDocument, {
            created: record => this.documentRestService.createWorkoutSessionDocument(record),
            updated: () => of(null),
            deleted: () => of(null),
            model: WorkoutSessionDocument,
        });
    }
    /**
     * Pulls a user library document from the server
     */
    pullUserLibrary() {
        return this.documentRestService.getUserLibraryDocumentIds().pipe(concatMap(uuids => {
            if (uuids && uuids.length > 0) {
                return this.getManyDocuments({
                    model: UserLibraryDocument,
                    clear: true,
                    uuids,
                    request: uuid => this.documentRestService.getUserLibraryDocument(uuid),
                    diffRequest: record => this.mergeChanges(record, this.documentRestService.getUserLibraryDocumentDiff(record), this.offline.getRecord(UserLibraryDocumentCache, record.uuid)),
                }).pipe(concatMap(lib => this.saveUserLibraryCache(lib)), map(() => null));
            }
            else {
                return of(null);
            }
        }));
    }
    mergeChanges(record, diffRequest, cacheRequest) {
        return diffRequest.pipe(concatMap(res => {
            record.version = res.version;
            if (res.patch.length > 0) {
                // if there are changes from the server, apply to cache and take diff of local record
                return cacheRequest.pipe(map(cache => {
                    // apply remote changes to local cache
                    const patched = patch(res.patch, cache.document);
                    // take diff of local document and patched cache
                    const diff$1 = diff(record.document, patched, diffOptions);
                    if (diff$1.length > 0) {
                        // if local document is still different from cache, apply diff to local document
                        record.document = patch(diff$1, record.document);
                    }
                    return record;
                }));
            }
            else {
                return of(record);
            }
        }));
    }
    /**
     * Marks a plan's sync status with the server.
     * @param uuid plan uuid
     */
    markDownloaded(uuid) {
        return this.offline.getRecord(PlanMetadata, uuid).pipe(concatMap(planMetadata => {
            if (planMetadata) {
                planMetadata.isDownloaded = true;
                return this.offline.save(PlanMetadata, planMetadata);
            }
            else {
                return of(null);
            }
        }));
    }
    saveCache(plans) {
        const cache = plans.map(plan => new PlanDocumentCache(plan));
        return this.offline.save(PlanDocumentCache, cache);
    }
    // TODO: make more generic
    saveUserLibraryCache(lib) {
        return this.offline.save(UserLibraryDocumentCache, lib.map(l => new UserLibraryDocumentCache(l)));
    }
    saveExerciseProgramDocumentCache(program) {
        return this.offline.save(ExerciseProgramDocumentCache, program.map(p => new ExerciseProgramDocumentCache(p)));
    }
    pullObservations() {
        return this.documentRestService.getObservationDocumentIds().pipe(concatMap(ids => {
            if (ids.length > 0) {
                return this.getManyDocuments({
                    model: ObservationDocument,
                    uuids: ids,
                    clear: true,
                    request: uuid => this.documentRestService.getObservationDocument(uuid),
                    diffRequest: record => of(record),
                });
            }
            else {
                return of([]);
            }
        }));
    }
    pushObservations() {
        return this.syncDirty(ObservationDocument, {
            created: record => this.documentRestService.createObservationDocument(record),
            updated: () => of(null),
            deleted: () => of(null),
            model: ObservationDocument,
        });
    }
}

const ajv = Ajv;
class DocumentModelValidationService {
    constructor() {
        this.initialized = false;
        this.init();
    }
    init() {
        if (!this.initialized) {
            this.validator = new Ajv();
            this.validator.addSchema(require('../schemas/equipment.schema.json'), 'equipment.schema');
            this.validator.addSchema(require('../schemas/exercise.schema.json'), 'exercise.schema');
            this.validator.addSchema(require('../schemas/restrict_label.schema.json'), 'restrict_label.schema');
            this.validator.addSchema(require('../schemas/exercise_parameters.schema.json'), 'exercise_parameters.schema');
            this.validator.addSchema(require('../schemas/resource.schema.json'), 'resource.schema#/definitions/video');
            this.validator.addSchema(require('../schemas/resource.schema.json'), 'resource.schema#/definitions/audio');
            this.validator.addSchema(require('../schemas/resource.schema.json'), 'resource.schema#/definitions/image');
            this.schema = this.validator.compile(require('../schemas/plan.schema.json'));
        }
        this.initialized = true;
    }
    validate(model, path) {
        const result = this.schema(model, path);
        return result === true;
    }
}
DocumentModelValidationService.ɵfac = function DocumentModelValidationService_Factory(t) { return new (t || DocumentModelValidationService)(); };
DocumentModelValidationService.ɵprov = ɵɵdefineInjectable({ token: DocumentModelValidationService, factory: DocumentModelValidationService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵsetClassMetadata(DocumentModelValidationService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], function () { return []; }, null); })();

/*
 * Public API Surface of fitforce-document-sync
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DocumentDatabaseService, DocumentModelFactoryService, DocumentModelFactoryServiceStub, DocumentModelType, DocumentModelValidationService, DocumentRestService, DocumentSyncService, ExerciseProgramDocument, ExerciseProgramDocumentCache, ExerciseProgramDocumentConstants, ExerciseProgramDocumentUtils, ObservationDocument, PlanDocument, PlanDocumentCache, PlanDocumentUtils, PlanMetadata, ResourceType, RestOptions, UserLibraryDocument, UserLibraryDocumentCache, WorkoutSessionDocument, ajv, getDefaultParams, rpeOptions, tierRowParams };
//# sourceMappingURL=fitforce-document-sync.js.map
